import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  getDay,
  isSameMonth,
  startOfWeek,
  parseISO,
} from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import IcalConfigDialog from "@/components/admin/IcalConfigDialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { copyToClipboard as clipboardCopy } from "@/lib/clipboard";
import { getToken, clearSession } from "@/lib/auth";
import { toast } from "sonner";
import UnassignedWalkinsBanner from "@/components/admin/UnassignedWalkinsBanner";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Link2,
  RefreshCcw,
  Copy,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Wand2,
  Tag,
  X,
  Sparkles,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  Mail,
  Lock,
} from "lucide-react";

const DAYS_FULL_IT = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"];

// === Status colors — strict legend (user-decided):
//   - VERDE       → libero
//   - ARANCIONE   → prenotato (vero ospite assegnato, qualsiasi fonte)
//   - ROSSO scuro → tutto il resto che vale "occupato/non vendibile":
//                   sold_out, blocked, external (no guest), ota_block
//   - I lucchetti A/B per Airbnb/Booking restano come overlay
const cellTone = (status /* , source */) => {
  switch (status) {
    case "booked":
      return "bg-orange-500 text-white border-orange-600";
    case "external":
    case "ota_block":
    case "blocked":
    case "sold_out":
      return "bg-rose-700 text-white border-rose-800";
    default:
      return "bg-emerald-500 text-white border-emerald-600";
  }
};


const pillTone = (status, source) => {
  if (status === "booked" && source === "walk_in") {
    return "bg-amber-100 text-amber-800 border-amber-300";
  }
  switch (status) {
    case "booked":
      return "bg-orange-100 text-orange-800 border-orange-300";
    case "external":
      return "bg-sky-100 text-sky-800 border-sky-300";
    case "ota_block":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "blocked":
      return "bg-rose-100 text-rose-800 border-rose-300";
    default:
      return "bg-emerald-100 text-emerald-800 border-emerald-300";
  }
};

const statusLabel = (s, source) =>
  (s === "booked" && source === "walk_in") ? "Walk-in"
  : ({ booked: "Prenotato", external: "Esterno", ota_block: "Blocco OTA", blocked: "Chiuso", free: "Libero" }[s] || s);

export default function AdminChannelManagerPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [overview, setOverview] = useState({ rooms: [] });
  const [loading, setLoading] = useState(true);
  const [icalConfig, setIcalConfig] = useState([]);
  const [icalDialogOpen, setIcalDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadingXls, setUploadingXls] = useState(false);
  const [pollingEmail, setPollingEmail] = useState(false);
  const [fullSyncing, setFullSyncing] = useState(false);
  const [activeDay, setActiveDay] = useState(null); // {date, items[]}
  const [ratePlans, setRatePlans] = useState([]);
  const [icalHealth, setIcalHealth] = useState({ failing: 0, items: [] });

  // Load active rate plans for price preview rows
  useEffect(() => {
    api.get("/admin/rate-plans")
      .then((r) => setRatePlans((r.data?.rate_plans || []).filter((p) => p.active !== false)))
      .catch(() => { /* non-blocking */ });
  }, []);

  // Load iCal feed health (background) so we can show a warning banner when
  // any of the configured Airbnb/Booking links is failing repeatedly.
  const loadIcalHealth = useCallback(() => {
    api.get("/admin/ical-health")
      .then((r) => setIcalHealth(r.data || { failing: 0, items: [] }))
      .catch(() => { /* non-blocking */ });
  }, []);
  useEffect(() => { loadIcalHealth(); }, [loadIcalHealth]);

  // Multi-cell selection across rooms+dates for bulk edit
  const [selectedCells, setSelectedCells] = useState({}); // { `${roomId}|${date}`: {roomId, roomSlug, date} }
  const [bulkOpen, setBulkOpen] = useState(false);

  // === DRAG SELECTION (Booking.com Extranet style) ===
  // While the user holds the mouse down (or touches and drags) across PMS
  // cells, every cell they cross is added to the selection. This is the
  // single biggest user request — typing values into one cell at a time
  // is unbearable for seasonal pricing/availability work.
  const dragModeRef = useRef(null); // null | "add" | "remove"
  const [dragActive, setDragActive] = useState(false);

  const startDragSelection = useCallback((room, day, e) => {
    if (!day?.date) return;
    if (e && e.preventDefault) e.preventDefault();
    const k = `${room.id}|${day.date}`;
    setSelectedCells((prev) => {
      const next = { ...prev };
      if (next[k]) {
        dragModeRef.current = "remove";
        delete next[k];
      } else {
        dragModeRef.current = "add";
        next[k] = {
          roomId: room.id, roomSlug: room.slug, roomName: room.name?.it, date: day.date,
          // snapshot of CURRENT values — used by the bulk-edit drawer to
          // show "min – max" hints (and avoid overwriting blindly).
          price: day.price ?? room.price_per_night ?? null,
          units_available: day.units_available ?? null,
          units_total: day.units_total ?? null,
          blocked: !!day.blocked,
          min_stay: day.min_stay ?? null,
          max_stay: day.max_stay ?? null,
          min_advance: day.min_advance_days ?? null,
          closed_to_arrival: !!day.closed_to_arrival,
          closed_to_departure: !!day.closed_to_departure,
          promo_label: day.promo_label || "",
          promo_discount_pct: day.promo_discount_pct ?? 0,
        };
      }
      return next;
    });
    setDragActive(true);
  }, []);

  const extendDragSelection = useCallback((room, day) => {
    if (!dragModeRef.current || !day?.date) return;
    const k = `${room.id}|${day.date}`;
    setSelectedCells((prev) => {
      const next = { ...prev };
      if (dragModeRef.current === "add") {
        if (!next[k]) {
          next[k] = {
            roomId: room.id, roomSlug: room.slug, roomName: room.name?.it, date: day.date,
            price: day.price ?? room.price_per_night ?? null,
            units_available: day.units_available ?? null,
            units_total: day.units_total ?? null,
            blocked: !!day.blocked,
            min_stay: day.min_stay ?? null,
            max_stay: day.max_stay ?? null,
            min_advance: day.min_advance_days ?? null,
            closed_to_arrival: !!day.closed_to_arrival,
            closed_to_departure: !!day.closed_to_departure,
            promo_label: day.promo_label || "",
            promo_discount_pct: day.promo_discount_pct ?? 0,
          };
        }
      } else if (dragModeRef.current === "remove") {
        if (next[k]) delete next[k];
      }
      return next;
    });
  }, []);

  const endDragSelection = useCallback(() => {
    dragModeRef.current = null;
    setDragActive(false);
  }, []);

  useEffect(() => {
    if (!dragActive) return;
    const onUp = () => endDragSelection();
    // While drag is active, disable any browser-native touch panning so the
    // table doesn't scroll horizontally under the finger. Without this, on
    // mobile/tablet the swipe is consumed by `overflow-x-auto` on the
    // scroll container instead of being routed to our cell handlers.
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.touchAction = "none";
    document.body.classList.add("pms-dragging");
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);
    return () => {
      document.body.style.touchAction = prevTouchAction;
      document.body.classList.remove("pms-dragging");
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
    };
  }, [dragActive, endDragSelection]);

  const cellKey = (roomId, date) => `${roomId}|${date}`;

  const toggleCell = (room, day) => {
    if (!day || !day.date) return;
    const k = cellKey(room.id, day.date);
    setSelectedCells((prev) => {
      const next = { ...prev };
      if (next[k]) {
        delete next[k];
      } else {
        next[k] = { roomId: room.id, roomSlug: room.slug, roomName: room.name?.it, date: day.date };
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedCells({});

  const selectedCount = Object.keys(selectedCells).length;

  // Derive a human-readable summary of the current selection so the action
  // bar shows "X celle · 5 → 12 Jun · 2 camere" instead of just "X celle".
  const selectionSummary = useMemo(() => {
    const vals = Object.values(selectedCells);
    if (!vals.length) return null;
    const dates = vals.map((v) => v.date).sort();
    const rooms = new Set(vals.map((v) => v.roomName).filter(Boolean));
    const fmt = (iso) => {
      try { return format(parseISO(iso), "dd/MM/yyyy"); }
      catch { return iso; }
    };
    const dRange = dates.length === 1 ? fmt(dates[0]) : `${fmt(dates[0])} → ${fmt(dates[dates.length - 1])}`;
    return { dRange, roomCount: rooms.size, roomLabel: rooms.size === 1 ? Array.from(rooms)[0] : null };
  }, [selectedCells]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    const from = format(startOfMonth(month), "yyyy-MM-dd");
    const to = format(endOfMonth(month), "yyyy-MM-dd");
    try {
      const res = await api.get("/admin/calendar-overview", {
        params: { from_date: from, to_date: to },
      });
      setOverview(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearSession();
        navigate("/admin/login");
      } else {
        toast.error("Errore caricamento panoramica");
      }
    } finally {
      setLoading(false);
    }
  }, [month, navigate]);

  const loadIcal = useCallback(async () => {
    try {
      const res = await api.get("/admin/ical-config");
      setIcalConfig(res.data);
    } catch (err) {
      console.error("Failed to load iCal config", err);
    }
  }, []);

  useEffect(() => {
    loadOverview();
    loadIcal();
  }, [loadOverview, loadIcal]);

  // === iter121: RATE-PLAN drag-select (close/open multiple days at once) ===
  // Separate state from the price/availability drag because:
  //   - rate-plan closures act on a SINGLE rate plan at a time (the one
  //     where the drag started). Crossing into another plan row mid-drag
  //     does NOT extend the selection — we keep the gesture predictable.
  //   - the "mode" depends on the starting cell: if it was OPEN we close
  //     everything we cross; if it was CLOSED we reopen.
  const [rpDrag, setRpDrag] = useState({
    active: false,
    mode: null,
    roomId: null,
    planId: null,
    dates: new Set(),
  });
  const rpDragRef = useRef(rpDrag);
  rpDragRef.current = rpDrag;

  const startRpDrag = useCallback((room, rp, day, isCurrentlyClosed, ev) => {
    if (!day?.date || !rp?.id) return;
    if (ev && ev.preventDefault) ev.preventDefault();
    setRpDrag({
      active: true,
      mode: isCurrentlyClosed ? "open" : "close",
      roomId: room.id,
      planId: rp.id,
      dates: new Set([day.date]),
    });
  }, []);

  const extendRpDrag = useCallback((room, rp, day) => {
    const cur = rpDragRef.current;
    if (!cur.active) return;
    if (cur.roomId !== room.id || cur.planId !== rp.id) return;
    if (!day?.date || cur.dates.has(day.date)) return;
    setRpDrag((prev) => ({
      ...prev,
      dates: new Set([...prev.dates, day.date]),
    }));
  }, []);

  const finishRpDrag = useCallback(async () => {
    const cur = rpDragRef.current;
    if (!cur.active) return;
    setRpDrag({ active: false, mode: null, roomId: null, planId: null, dates: new Set() });
    if (!cur.dates.size) return;
    const dates = Array.from(cur.dates).sort();
    const from_date = dates[0];
    const to_date = dates[dates.length - 1];
    const patch = cur.mode === "close"
      ? { closed_rate_plans: [cur.planId] }
      : { reopen_rate_plans: [cur.planId] };
    try {
      const res = await api.put(`/admin/calendar/${cur.roomId}/bulk`, {
        from_date, to_date, ...patch,
      });
      const updated = res?.data?.updated ?? dates.length;
      toast.success(cur.mode === "close"
        ? `🔒 Tariffa chiusa per ${updated} giorni`
        : `🔓 Tariffa riaperta per ${updated} giorni`,
        { duration: 1800 });
      loadOverview();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento tariffa"));
    }
  }, [loadOverview]);

  useEffect(() => {
    if (!rpDrag.active) return;
    const handler = () => finishRpDrag();
    const prevTouch = document.body.style.touchAction;
    document.body.style.touchAction = "none";
    window.addEventListener("mouseup", handler);
    window.addEventListener("touchend", handler);
    window.addEventListener("touchcancel", handler);
    return () => {
      document.body.style.touchAction = prevTouch;
      window.removeEventListener("mouseup", handler);
      window.removeEventListener("touchend", handler);
      window.removeEventListener("touchcancel", handler);
    };
  }, [rpDrag.active, finishRpDrag]);

  const updateRoomMeta = useCallback(async (roomId, patch) => {
    try {
      await api.put(`/admin/rooms/${roomId}`, patch);
      toast.success("Camera aggiornata");
      loadOverview();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento camera"));
    }
  }, [loadOverview]);

  const updateCell = useCallback(async (room, dateOrRange, patch) => {
    try {
      const isRange = dateOrRange && typeof dateOrRange === "object" && dateOrRange.from && dateOrRange.to;
      const from_date = isRange ? dateOrRange.from : dateOrRange;
      const to_date = isRange ? dateOrRange.to : dateOrRange;
      const res = await api.put(`/admin/calendar/${room.id}/bulk`, {
        from_date,
        to_date,
        ...patch,
      });
      const updated = res?.data?.updated ?? 1;
      // iter120: friendlier toast when toggling rate-plan closures
      if (patch.closed_rate_plans?.length) {
        toast.success(`🔒 Tariffa chiusa per ${isRange ? `${updated} giorni` : "1 giorno"}`, { duration: 1800 });
      } else if (patch.reopen_rate_plans?.length) {
        toast.success(`🔓 Tariffa riaperta per ${isRange ? `${updated} giorni` : "1 giorno"}`, { duration: 1800 });
      } else {
        toast.success(isRange ? `Aggiornati ${updated} giorni` : "Aggiornato", { duration: 1500 });
      }
      loadOverview();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento"));
    }
  }, [loadOverview]);

  // Double-click on a cell in the overview grid: quick block/unblock toggle.
  // Skips booked/external dates — those must be handled from the booking detail.
  const toggleCellBlock = useCallback(async (room, day) => {
    if (!day?.date) return;
    if (day.status === "booked") {
      toast.info(
        `Già prenotata da ${day.guest || "ospite"}${day.code ? ` · ${day.code}` : ""}. Per modificare apri il dettaglio della prenotazione.`,
        { duration: 3500 }
      );
      return;
    }
    if (day.status === "external") {
      toast.info(
        `Già occupata da ${day.source || "canale esterno"}${day.summary ? ` · ${day.summary}` : ""}. Modifica dal canale di origine (Booking/Airbnb).`,
        { duration: 3500 }
      );
      return;
    }
    if (day.status === "ota_block") {
      toast.info(
        `Data chiusa su ${day.source || "Airbnb/Booking"}. Riapri dal pannello del canale di origine (Booking/Airbnb extranet).`,
        { duration: 3500 }
      );
      return;
    }
    const willBlock = day.status !== "blocked";
    try {
      await api.put(`/admin/calendar/${room.id}/bulk`, {
        from_date: day.date,
        to_date: day.date,
        blocked: willBlock,
      });
      toast.success(willBlock ? `🔴 ${day.date} chiusa` : `✓ ${day.date} riaperta`, { duration: 1800 });
      loadOverview();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento"));
    }
  }, [loadOverview]);

  const monthDays = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfMonth(month),
        end: endOfMonth(month),
      }),
    [month]
  );

  // --- Aggregated day map: per date string -> array of {room, day} ---
  const bookingsByDate = useMemo(() => {
    const map = {};
    overview.rooms.forEach((r) => {
      r.days.forEach((d) => {
        if (d.status === "booked" || d.status === "external") {
          if (!map[d.date]) map[d.date] = [];
          map[d.date].push({ room: r, day: d });
        }
      });
    });
    return map;
  }, [overview]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/admin/ical-sync-now");
      const data = res.data || {};
      const totalEvents = data.total_events || 0;
      const allWarnings = [];
      const allErrors = [];
      (data.results || []).forEach((r) => {
        (r.warnings || []).forEach((w) => allWarnings.push({ ...w, room_slug: r.room_slug }));
        (r.errors || []).forEach((e) => allErrors.push({ ...e, room_slug: r.room_slug }));
      });
      if (allErrors.length > 0) {
        // Errors after backend retried 3× — these are likely real problems.
        // Still avoid the alarming "TOKEN NON VALIDO" copy for the first
        // confirmed failure since Booking.com often recovers within a few
        // minutes. The persistent banner under the header will surface
        // chronic problems via the `consecutive_failures` threshold.
        const total = allErrors.length;
        toast.warning(
          `${totalEvents} eventi importati · ${total} link non raggiungibili in questo tentativo (verranno riprovati automaticamente al prossimo ciclo).`,
          { duration: 8000 },
        );
      } else {
        toast.success(`Sync completato: ${totalEvents} eventi importati`);
      }
      await Promise.all([loadOverview(), loadIcal()]);
      loadIcalHealth();
    } catch (err) {
      toast.error(errMsg(err, "Errore durante la sincronizzazione"));
    } finally {
      setSyncing(false);
    }
  };

  const copyToClipboard = async (text) => {
    const ok = await clipboardCopy(text);
    if (ok) toast.success("URL copiato");
    else toast.error("Copia non riuscita");
  };

  const uploadBookingXls = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingXls(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/bookings/import-booking-xls", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      const errCount = (d.errors || []).length;
      toast.success(
        `XLS importato: ${d.created || 0} nuove · ${d.updated || 0} aggiornate · ${d.merged_externals || 0} blocchi iCal uniti${errCount ? ` · ${errCount} righe non lette` : ""}`,
        { duration: 8000 },
      );
      await Promise.all([loadOverview(), loadIcal()]);
    } catch (err) {
      toast.error(errMsg(err, "Errore durante l'import XLS"));
    } finally {
      setUploadingXls(false);
    }
  };

  const pollBookingEmail = async (opts = {}) => {
    const { days = 14, includeRead = false } = opts;
    setPollingEmail(true);
    try {
      const params = new URLSearchParams();
      if (days !== 14) params.set("days", String(days));
      if (includeRead) params.set("include_read", "true");
      const url = `/admin/bookings/poll-booking-email${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await api.post(url);
      const d = res.data || {};
      const lines = [];
      if (d.created) lines.push(`${d.created} nuove`);
      if (d.updated) lines.push(`${d.updated} aggiornate`);
      if (d.cancelled) lines.push(`${d.cancelled} cancellate`);
      if (!lines.length) lines.push("nessuna email nuova");
      const modeLabel = includeRead ? ` · backfill ${days}gg incl. già lette` : "";
      toast.success(
        `📧 Sync email Booking${modeLabel}: ${lines.join(" · ")} (${d.checked || 0} controllate)`,
        { duration: 8000 },
      );
      await Promise.all([loadOverview(), loadIcal()]);
    } catch (err) {
      toast.error(errMsg(err, "Errore durante il sync email"));
    } finally {
      setPollingEmail(false);
    }
  };

  // Re-import last 30 days INCLUDING already-read emails. Useful when the
  // admin opened a Booking notification on their phone (Gmail marks it as
  // read) before the cron managed to pick it up. Idempotent.
  const reimportBookingEmails = async () => {
    if (!window.confirm(
      "Ri-scansiono tutte le email Booking.com degli ultimi 30 giorni — incluse quelle già aperte.\n\n" +
      "Le prenotazioni già presenti non vengono duplicate (match per numero prenotazione). " +
      "Procedo?"
    )) return;
    await pollBookingEmail({ days: 30, includeRead: true });
  };

  const fullSync = async () => {
    setFullSyncing(true);
    try {
      // 1) iCal feeds
      const ical = await api.post("/admin/ical-sync");
      // 2) Booking inbox email
      let email = { created: 0, updated: 0 };
      try {
        const r = await api.post("/admin/bookings/poll-booking-email");
        email = r.data || {};
      } catch { /* casella non configurata: skip silenziosamente */ }
      // 3) Cross-channel enrichment (merge name/price/date from any sibling)
      const enrich = await api.post("/admin/bookings/enrich-all");
      const e = enrich.data || {};
      // 3b) Pull customer contact info (email/phone) from App Guida
      let contacts = { bookings_enriched: 0, fields_filled: 0 };
      try {
        const r4 = await api.post("/admin/bookings/pull-guide-customers");
        contacts = r4.data || {};
      } catch { /* ignore */ }
      // 3b) Auto-promote status by date (confirmed→checked_in→completed)
      let promo = { promoted_checkin: 0, promoted_completed: 0 };
      try {
        const r3 = await api.post("/admin/bookings/auto-promote-statuses");
        promo = r3.data || {};
      } catch { /* ignore */ }
      // 4) Cleanup empty cancelled stubs (legacy data)
      let cleaned = 0;
      try {
        const r2 = await api.post("/admin/bookings/cleanup-empty-cancelled");
        cleaned = r2.data?.deleted || 0;
      } catch { /* ignore */ }

      const promoTxt = (promo.promoted_checkin || promo.promoted_completed)
        ? ` · 🚦 Status: ${promo.promoted_checkin || 0} check-in, ${promo.promoted_completed || 0} completate`
        : "";
      const contactsTxt = contacts.bookings_enriched
        ? ` · 📞 Contatti: ${contacts.bookings_enriched} ospiti (${contacts.fields_filled} campi)`
        : "";
      const cleanedTxt = cleaned > 0 ? ` · 🧹 Rimosse ${cleaned} cancellate vuote` : "";
      toast.success(
        `🔄 Sincronizzazione completa · iCal: ${(ical.data?.imported || 0)} nuove, ${(ical.data?.updated || 0)} aggior. · Email: ${email.created || 0} nuove · Arricchimento: ${e.enriched || 0} prenotazioni (${e.fields_filled || 0} campi)${contactsTxt}${promoTxt}${cleanedTxt}`,
        { duration: 10000 },
      );
      await Promise.all([loadOverview(), loadIcal()]);
    } catch (err) {
      toast.error(errMsg(err, "Errore durante la sincronizzazione completa"));
    } finally {
      setFullSyncing(false);
    }
  };

  // ============================================================
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-[#1E3A5F] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-10 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 shrink-0">
            <Button
              onClick={() => navigate("/admin/dashboard")}
              variant="outline"
              size="sm"
              data-testid="back-dashboard"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white shrink-0"
            >
              <ArrowLeft className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Dashboard</span>
            </Button>
            <div className="shrink-0">
              <h1 className="font-display text-xl md:text-2xl">PMS</h1>
              <p className="text-[10px] text-white/60 hidden xl:block">
                Sync Airbnb / Booking.com
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              onClick={() => navigate("/admin/calendar?action=bulk")}
              variant="outline"
              size="sm"
              data-testid="cm-goto-bulk"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
            >
              <Wand2 className="w-4 h-4 md:mr-2" />
              <span className="hidden xl:inline">Modifica in blocco</span>
              <span className="hidden md:inline xl:hidden">Blocco</span>
            </Button>
            <Button
              onClick={() => navigate("/admin/calendar?action=presets")}
              variant="outline"
              size="sm"
              data-testid="cm-goto-presets"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
            >
              <Sparkles className="w-4 h-4 md:mr-2" />
              <span className="hidden xl:inline">Preset stagionali</span>
              <span className="hidden md:inline xl:hidden">Preset</span>
            </Button>
            <Button
              onClick={() => setIcalDialogOpen(true)}
              variant="outline"
              size="sm"
              data-testid="open-ical-config"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
            >
              <Link2 className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">iCal</span>
            </Button>
            <Button
              onClick={fullSync}
              disabled={fullSyncing || syncing || pollingEmail}
              size="sm"
              data-testid="full-sync-all"
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-semibold shadow-md"
              title="Esegue in sequenza: iCal · Email Booking · Cross-channel enrichment (riempie nomi, prezzi, date mancanti da tutti i canali)"
            >
              <Sparkles className={`w-4 h-4 md:mr-2 ${fullSyncing ? "animate-spin" : ""}`} />
              <span className="hidden xl:inline">{fullSyncing ? "Sincronizzazione…" : "Sincronizza tutto"}</span>
              <span className="hidden md:inline xl:hidden">Tutto</span>
            </Button>
            <Button
              onClick={triggerSync}
              disabled={syncing}
              size="sm"
              data-testid="sync-now"
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
            >
              <RefreshCcw className={`w-4 h-4 md:mr-2 ${syncing ? "animate-spin" : ""}`} />
              <span className="hidden xl:inline">{syncing ? "Sync..." : "Sincronizza ora"}</span>
              <span className="hidden md:inline xl:hidden">Sync</span>
            </Button>
            <div className="inline-flex rounded-md overflow-hidden border border-white/30">
              <Button
                onClick={() => pollBookingEmail()}
                disabled={pollingEmail}
                variant="outline"
                size="sm"
                data-testid="poll-booking-email"
                className="bg-transparent border-0 text-white hover:bg-white/10 hover:text-white rounded-none"
                title="Controlla email Booking.com (Gmail) non lette degli ultimi 14 giorni"
              >
                <Mail className={`w-4 h-4 md:mr-2 ${pollingEmail ? "animate-pulse" : ""}`} />
                <span className="hidden xl:inline">{pollingEmail ? "Lettura..." : "Sync Email"}</span>
                <span className="hidden md:inline xl:hidden">Email</span>
              </Button>
              <button
                onClick={reimportBookingEmails}
                disabled={pollingEmail}
                data-testid="reimport-booking-email"
                title="Re-importa ultimi 30 giorni — include anche email Booking già lette (utile se aperte sul cellulare prima del cron)"
                className="px-2 border-l border-white/30 text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-40 text-xs"
              >
                ⟳
              </button>
            </div>
            <label
              htmlFor="xls-booking-upload"
              className="inline-flex items-center justify-center cursor-pointer rounded-md px-3 h-9 text-sm font-medium bg-[#1E3A5F] hover:bg-[#16304F] text-white border border-white/30"
              data-testid="import-booking-xls-trigger"
              title="Importa file Arrivo_*.xls da Booking.com extranet per popolare nomi clienti e dettagli"
            >
              <FileSpreadsheet className="w-4 h-4 md:mr-2" />
              <span className="hidden xl:inline">{uploadingXls ? "Importazione..." : "Importa XLS Booking"}</span>
              <span className="hidden md:inline xl:hidden">XLS</span>
              <input
                id="xls-booking-upload"
                type="file"
                accept=".xls,.xlsx"
                className="hidden"
                disabled={uploadingXls}
                onChange={uploadBookingXls}
                data-testid="import-booking-xls-input"
              />
            </label>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-3 md:px-6 py-5 md:py-8">
        {/* iCal health alert: only shown when at least one feed is failing */}
        {icalHealth.failing > 0 && (
          <div
            className="mb-5 rounded-lg border-2 border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900"
            data-testid="ical-health-banner"
          >
            <div className="font-semibold flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <span className="text-base">🚨</span>
                <span>{icalHealth.failing} link iCal NON funzionano</span>
              </span>
              <button
                onClick={async () => {
                  try {
                    await api.post("/admin/ical-health/dismiss");
                    toast.success("Avvisi ignorati. Riapparirà al prossimo errore.");
                    loadIcalHealth();
                  } catch (err) {
                    toast.error(errMsg(err, "Errore"));
                  }
                }}
                className="text-[11px] underline text-rose-700 hover:text-rose-900 font-medium"
                data-testid="ical-health-dismiss"
                title="Nasconde questi avvisi. Riappariranno solo se la prossima sincronizzazione fallisce di nuovo."
              >
                ✕ Ignora avvisi
              </button>
            </div>
            <ul className="mt-2 ml-6 list-disc text-[12px] space-y-0.5">
              {icalHealth.items
                .filter((it) => it.status === "error")
                .slice(0, 6)
                .map((it) => (
                  <li key={it.url}>
                    <b>{it.source || "OTA"}</b> · camera <b>{it.room_slug}</b>:
                    <span className="ml-1 italic">{it.last_error?.slice(0, 80)}</span>
                  </li>
                ))}
            </ul>
            <div className="mt-2 text-[12px]">
              ➡️ Su <b>{icalHealth.items.find((it) => it.status === "error")?.source || "Booking/Airbnb"}</b> extranet rigenera i link e salvali nel pannello (icona 🔗 in alto). Ricontrolla con "Sincronizza ora".
            </div>
          </div>
        )}
        {/* Walk-in dall'App Guida senza camera assegnata */}
        <div className="mb-5">
          <UnassignedWalkinsBanner />
        </div>
        {/* Month nav + inline action bar (top) */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setMonth(subMonths(month, 1))}
              data-testid="overview-prev-month"
              aria-label="Mese precedente"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div
              className="font-display text-2xl md:text-3xl text-[#1E3A5F] capitalize text-center min-w-[180px]"
              data-testid="overview-month-label"
            >
              {format(month, "MMMM yyyy", { locale: itLocale })}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setMonth(addMonths(month, 1))}
              data-testid="overview-next-month"
              aria-label="Mese successivo"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          {selectedCount > 0 && (
            <div
              className="flex items-center gap-2 bg-[#1E3A5F] text-white rounded-full pl-4 pr-1.5 py-1 shadow-md animate-fadeUp"
              data-testid="cm-action-bar"
            >
              <span className="text-xs leading-tight flex flex-col">
                <span>
                  <strong className="text-[#E3A857]">{selectedCount}</strong>{" "}
                  {selectedCount === 1 ? "cella" : "celle"}
                  {selectionSummary && (
                    <span className="text-white/70"> · {selectionSummary.dRange}</span>
                  )}
                </span>
                {selectionSummary && selectionSummary.roomLabel && (
                  <span className="text-[10px] text-white/60 italic truncate max-w-[180px]">
                    {selectionSummary.roomLabel}
                  </span>
                )}
                {selectionSummary && !selectionSummary.roomLabel && selectionSummary.roomCount > 1 && (
                  <span className="text-[10px] text-white/60 italic">
                    {selectionSummary.roomCount} camere
                  </span>
                )}
              </span>
              <Button
                size="sm"
                onClick={() => setBulkOpen(true)}
                data-testid="cm-bulk-edit"
                className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-8 px-3 text-xs"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Modifica
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={clearSelection}
                data-testid="cm-clear-selection"
                className="h-8 w-8 rounded-full text-white hover:bg-white/10"
                aria-label="Pulisci selezione"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Legend */}
        <Legend />

        {/* Helpful tip for users new to the drag-to-select interaction */}
        {selectedCount === 0 && (
          <div className="mb-2 -mt-2 text-[11px] text-[#635F59] flex items-center gap-1.5 px-2 flex-wrap">
            <span className="bg-[#E3A857]/20 text-[#8B6914] px-2 py-0.5 rounded-full font-semibold text-[10px]">SUGGERIMENTO</span>
            <span>
              <b>Clicca</b> una cella per modificarla · <b>Trascina</b> mouse/dito su più celle per selezione multipla · poi clicca <b>Modifica</b> per applicare prezzo / disponibilità a tutto il range. · <b className="text-rose-700">Trascina</b> mouse/dito su una riga tariffa (Standard / Flessibile / Non rimborsabile / Mensile) per <b>chiudere o riaprire</b> quella tariffa sui giorni attraversati. Solo Standard è modificabile in prezzo (un click); le altre sono calcolate automaticamente.
            </span>
          </div>
        )}

        {/* === DESKTOP: PMS-style grid (rooms x days) === */}
        <div className="hidden md:block">
          <PmsGrid
            month={month}
            monthDays={monthDays}
            rooms={overview.rooms}
            loading={loading}
            selected={selectedCells}
            onToggleCell={toggleCell}
            onDoubleClickCell={toggleCellBlock}
            onUpdateRoom={updateRoomMeta}
            onUpdateCell={updateCell}
            ratePlans={ratePlans}
            onStartDragSel={startDragSelection}
            onExtendDragSel={extendDragSelection}
            rpDrag={rpDrag}
            onStartRpDrag={startRpDrag}
            onExtendRpDrag={extendRpDrag}
          />
        </div>

        {/* === MOBILE: only Disponibilità (Prenotazioni is in the sidebar) === */}
        <div className="md:hidden space-y-6">
          {overview.rooms.map((room) => (
            <RoomMiniCalendar
              key={room.id}
              room={room}
              month={month}
              monthDays={monthDays}
              selected={selectedCells}
              onToggleCell={toggleCell}
              onDoubleClickCell={toggleCellBlock}
              onStartDragSel={startDragSelection}
              onExtendDragSel={extendDragSelection}
            />
          ))}
        </div>

        {loading && overview.rooms.length === 0 && (
          <p className="text-center text-[#635F59] py-8">Caricamento…</p>
        )}
      </main>

      {/* === iCal Config Dialog === */}
      <IcalConfigDialog
        open={icalDialogOpen}
        onOpenChange={setIcalDialogOpen}
        rooms={icalConfig}
        onSaved={loadIcal}
        onCopy={copyToClipboard}
      />

      {/* === Day details dialog (mobile bookings tab) === */}
      <Dialog open={!!activeDay} onOpenChange={(v) => !v && setActiveDay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1E3A5F]">
              {activeDay
                ? format(new Date(activeDay.date), "EEEE d MMMM yyyy", { locale: itLocale })
                : ""}
            </DialogTitle>
            <DialogDescription>
              {activeDay && (activeDay.items?.length || 0) === 0
                ? "Nessuna prenotazione per questo giorno"
                : `${activeDay?.items?.length || 0} prenotazion${
                    (activeDay?.items?.length || 0) === 1 ? "e" : "i"
                  }`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {(activeDay?.items || []).map((it, idx) => (
              <div
                key={`${it.room.id}-${it.day.date}`}
                className={`p-3 rounded-lg border ${pillTone(it.day.status, it.day.booking_source)}`}
                data-testid={`day-detail-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{it.room.name?.it}</span>
                  <span className="text-xs uppercase tracking-wider">
                    {it.day.status === "external"
                      ? it.day.source || "Esterno"
                      : statusLabel(it.day.status, it.day.booking_source)}
                  </span>
                </div>
                <div className="text-sm mt-1">
                  {it.day.guest || it.day.summary || it.day.code || "—"}
                </div>
                {it.day.code && (
                  <div className="text-xs opacity-70 mt-0.5">Codice: {it.day.code}</div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* === Bulk-edit drawer === */}
      <BulkEditDrawer
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        cells={Object.values(selectedCells)}
        onApplied={() => {
          setBulkOpen(false);
          clearSelection();
          loadOverview();
        }}
      />
    </div>
  );
}

// ============================================================
//  Components
// ============================================================

function Legend() {
  // Square swatches that match the cell shape (no rounded dots).
  return (
    <div className="space-y-1 mb-4 md:mb-6">
      <div className="flex flex-wrap gap-3 md:gap-5 text-xs text-[#635F59] items-center">
        <span className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 bg-emerald-500 border border-emerald-700" />
          Libero
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 bg-orange-500 border border-orange-700" />
          Prenotato
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 bg-rose-500 border border-rose-700 flex items-center justify-center text-white text-[8px] font-bold">✕</span>
          Sold-out / blocco manuale
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold rounded-md px-1 py-0.5 border-2 bg-pink-100 border-pink-400 text-pink-700">
            <Lock className="w-2 h-2 text-amber-500" strokeWidth={3} />A
          </span>
          Blocco Airbnb
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold rounded-md px-1 py-0.5 border-2 bg-sky-100 border-sky-400 text-sky-700">
            <Lock className="w-2 h-2 text-amber-500" strokeWidth={3} />B
          </span>
          Blocco Booking
        </span>
      </div>
      {/* Second row: source letter codes used on the "Camera fisica" hover */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#635F59] items-center">
        <span className="font-semibold text-[#1E3A5F]">Fonte:</span>
        <span className="flex items-center gap-1">
          <span className="text-orange-600 font-extrabold">W</span> walk-in
        </span>
        <span className="flex items-center gap-1">
          <span className="text-emerald-700 font-extrabold">S</span> sito diretta
        </span>
        <span className="flex items-center gap-1">
          <span className="text-sky-700 font-extrabold">B</span> Booking
        </span>
        <span className="flex items-center gap-1">
          <span className="text-pink-700 font-extrabold">A</span> Airbnb
        </span>
        <span className="text-[#AC5D45] italic">
          es <strong className="not-italic">n13 B Souite · 2p</strong>
        </span>
      </div>
    </div>
  );
}

// --- Desktop PMS-style grid ----------------------------------
function PmsGrid({ month, monthDays, rooms, loading, selected, onToggleCell, onDoubleClickCell, onUpdateRoom, onUpdateCell, ratePlans = [], onStartDragSel, onExtendDragSel, rpDrag, onStartRpDrag, onExtendRpDrag }) {
  // First room expanded by default, others collapsed. Persist user toggles in
  // local state during the session.
  const [openMap, setOpenMap] = useState({});
  const isOpen = (id) => (openMap[id] === undefined ? false : openMap[id]);
  const toggle = (id) => setOpenMap((p) => ({ ...p, [id]: !isOpen(id) }));
  const expandAll = () => setOpenMap(Object.fromEntries(rooms.map((r) => [r.id, true])));
  const collapseAll = () => setOpenMap(Object.fromEntries(rooms.map((r) => [r.id, false])));

  // Initial: first room open
  useEffect(() => {
    if (rooms.length && Object.keys(openMap).length === 0) {
      setOpenMap({ [rooms[0].id]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms.length]);

  return (
    <div className="space-y-3" data-testid="pms-grid">
      {rooms.length > 0 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button
            type="button"
            onClick={expandAll}
            className="text-[#AC5D45] hover:underline font-medium"
            data-testid="pms-expand-all"
          >
            Espandi tutte
          </button>
          <span className="text-[#E8E2D9]">·</span>
          <button
            type="button"
            onClick={collapseAll}
            className="text-[#635F59] hover:underline"
            data-testid="pms-collapse-all"
          >
            Comprimi tutte
          </button>
        </div>
      )}
      {rooms.map((room) => (
        room.is_virtual ? null : (
        <RoomTable
          key={room.id}
          room={room}
          monthDays={monthDays}
          selected={selected}
          isOpen={isOpen(room.id)}
          onToggle={() => toggle(room.id)}
          onToggleCell={onToggleCell}
          onDoubleClickCell={onDoubleClickCell}
          onUpdateRoom={onUpdateRoom}
          onUpdateCell={onUpdateCell}
          ratePlans={ratePlans}
          onStartDragSel={onStartDragSel}
          onExtendDragSel={onExtendDragSel}
          rpDrag={rpDrag}
          onStartRpDrag={onStartRpDrag}
          onExtendRpDrag={onExtendRpDrag}
        />
        )
      ))}
      {!loading && rooms.length === 0 && (
        <p className="text-center text-[#635F59] py-8">Nessuna camera trovata.</p>
      )}
      <p className="text-[11px] text-[#635F59] text-center">
        💡 Clicca sull'intestazione di una camera per <strong>espandere</strong>/<strong>comprimere</strong> i suoi dati · <strong>Doppio click su una cella</strong> per chiudere/riaprire rapidamente quel giorno · Doppio click su Prezzo / Min / Disponibilità per modificare al volo
      </p>
    </div>
  );
}

function UnassignedAlertRow({ room }) {
  // Compact warning row shown at the top of the Channel Manager when the Guide
  // App webhook delivered a check-in that we couldn't auto-assign to a real
  // room category. The user should open the booking and assign a room.
  const occupied = (room.days || []).filter((d) => d.code);
  if (occupied.length === 0) return null;
  // Group consecutive days that share the same booking code into a single bar
  const groups = [];
  let cur = null;
  for (const d of occupied) {
    if (cur && cur.code === d.code && cur.guest === d.guest) {
      cur.days.push(d);
    } else {
      cur = { code: d.code, guest: d.guest, source: d.booking_source, days: [d] };
      groups.push(cur);
    }
  }
  return (
    <div className="border-2 border-rose-300 bg-rose-50 rounded-2xl overflow-hidden shadow-sm" data-testid="unassigned-alert">
      <div className="px-4 py-3 bg-rose-100 border-b border-rose-200 flex items-center gap-2">
        <span className="text-xl">⚠️</span>
        <div className="flex-1">
          <div className="font-semibold text-rose-900 text-sm">Da assegnare ({groups.length} prenotaz.)</div>
          <div className="text-[11px] text-rose-700">Auto-assegnazione camera fallita per queste prenotazioni — aprile dalla tab Check-in e scegli una camera fisica</div>
        </div>
      </div>
      <div className="p-3 flex flex-wrap gap-2">
        {groups.map((g) => (
          <div
            key={g.code}
            className="bg-white border border-rose-300 rounded-lg px-2 py-1 text-xs flex items-center gap-2"
            title={`${g.guest || g.code} · ${g.days.length} giorn${g.days.length === 1 ? "o" : "i"} (${g.days[0].date}${g.days.length > 1 ? ` → ${g.days[g.days.length - 1].date}` : ""}) · da: ${g.source || "ota"}`}
          >
            <span className="font-mono text-[10px] bg-rose-100 px-1 rounded text-rose-800">{g.code}</span>
            <span className="font-semibold text-rose-900">{g.guest || "—"}</span>
            <span className="text-[10px] text-rose-600">{g.days.length}n</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoomTable({ room, monthDays, selected, isOpen, onToggle, onToggleCell, onDoubleClickCell, onUpdateRoom, onUpdateCell, ratePlans = [], onStartDragSel, onExtendDragSel, rpDrag, onStartRpDrag, onExtendRpDrag }) {  return (
    <div
      className="border border-[#E8E2D9] rounded-2xl overflow-hidden bg-white shadow-sm"
      data-testid={`room-table-${room.slug}`}
    >
      {/* Sticky room header (now clickable to collapse/expand) */}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`room-toggle-${room.slug}`}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E8E2D9] transition text-left ${
          isOpen ? "bg-[#1E3A5F]/10" : "bg-[#1E3A5F]/5 hover:bg-[#1E3A5F]/10"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isOpen
            ? <ChevronUp className="w-4 h-4 text-[#AC5D45] shrink-0" />
            : <ChevronDown className="w-4 h-4 text-[#635F59] shrink-0" />}
          <span className="font-semibold text-[#1E3A5F] truncate">{room.name?.it}</span>
          <span className="text-[11px] text-[#635F59] ml-1 hidden sm:inline">
            {room.capacity} ospiti · €{room.price_per_night}/notte
          </span>
        </div>
        <div
          className="flex items-center gap-2"
          // Prevent click on input from collapsing the section
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <span className="text-[11px] text-[#635F59] hidden md:inline">Unità in vendita:</span>
          <input
            type="number"
            min="1"
            max="20"
            defaultValue={room.units_count}
            key={`uc-${room.id}-${room.units_count}`}
            onBlur={(e) => {
              const v = parseInt(e.target.value);
              if (v && v !== room.units_count) onUpdateRoom?.(room.id, { units_count: v });
            }}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            data-testid={`units-count-${room.slug}`}
            className="w-14 h-7 text-center text-sm border border-[#E8E2D9] rounded bg-white"
            title="Numero totale di unità identiche per questa categoria"
          />
        </div>
      </button>
      {/* Per-room scrollable area — only render when expanded for performance */}
      {isOpen && (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-[#FDFBF7] border-b border-[#E8E2D9]">
              <th className="sticky left-0 bg-[#FDFBF7] z-10 text-left uppercase tracking-wider text-[#635F59] font-semibold px-2 py-2 min-w-[110px] border-r border-[#E8E2D9]">
                Riga
              </th>
              {monthDays.map((d) => {
                const isWeekend = getDay(d) === 0 || getDay(d) === 6;
                return (
                  <th
                    key={d.toISOString()}
                    className={`text-center font-semibold py-2 px-0 min-w-[30px] border-r border-[#E8E2D9] ${
                      isToday(d)
                        ? "bg-[#1E3A5F] text-white"
                        : isWeekend
                        ? "bg-[#F7F4EF] text-[#1E3A5F]"
                        : "text-[#635F59]"
                    }`}
                  >
                    <div className="opacity-60 text-[9px]">
                      {DAYS_FULL_IT[(getDay(d) + 6) % 7][0]}
                    </div>
                    <div className="text-sm">{format(d, "d")}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <RoomRows
              room={room}
              monthDays={monthDays}
              selected={selected}
              onToggleCell={onToggleCell}
              onDoubleClickCell={onDoubleClickCell}
              onUpdateCell={onUpdateCell}
              ratePlans={ratePlans}
              onStartDragSel={onStartDragSel}
              onExtendDragSel={onExtendDragSel}
              rpDrag={rpDrag}
              onStartRpDrag={onStartRpDrag}
              onExtendRpDrag={onExtendRpDrag}
            />
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function RoomRows({ room, monthDays, selected, onToggleCell, onDoubleClickCell, onUpdateCell, ratePlans = [], onStartDragSel, onExtendDragSel, rpDrag, onStartRpDrag, onExtendRpDrag }) {
  const cellKey = (date) => `${room.id}|${date}`;
  // Each section (units, price, minstay, rest) is expandable.
  // Status is ALWAYS shown (no dropdown — user-locked).
  const [expanded, setExpanded] = useState({
    units: false, price: false, minstay: false, rest: false,
  });
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  // Bulk clear an entire restriction row for the visible month
  const clearRow = (patch, label) => {
    if (!room.days.length) return;
    const fromDate = room.days[0].date;
    const toDate = room.days[room.days.length - 1].date;
    if (!window.confirm(`Pulire "${label}" da ${fromDate} a ${toDate} per ${typeof room.name === 'string' ? room.name : room.slug}?`)) return;
    onUpdateCell?.(room, { from: fromDate, to: toDate }, patch);
  };

  const SectionLabel = ({ id, children, hint, locked = false }) => (
    <td
      className={`sticky left-0 bg-white z-10 px-2 py-1.5 border-r border-[#E8E2D9] text-[#1E3A5F] ${locked ? "" : "cursor-pointer hover:bg-[#FDFBF7]"}`}
      onClick={locked ? undefined : () => toggle(id)}
      data-testid={`pms-section-${room.slug}-${id}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider">
        {locked
          ? <span className="w-3 h-3 inline-block" aria-hidden="true" />
          : expanded[id]
            ? <ChevronDown className="w-3 h-3 text-[#AC5D45]" />
            : <ChevronRight className="w-3 h-3 text-[#635F59]" />}
        <span>{children}</span>
      </div>
      {hint && (locked || expanded[id]) && <div className="text-[9px] text-[#635F59] normal-case mt-0.5">{hint}</div>}
    </td>
  );

  return (
    <>
      {/* ==== STATUS group (locked open, no dropdown) ==== */}
      <tr className="border-b border-[#E8E2D9]">
        <SectionLabel id="status" locked>Status</SectionLabel>
        {room.days.map((d) => {
          const isSel = !!selected?.[cellKey(d.date)];
          const tone = isSel
            ? "bg-[#AC5D45] text-white border-[#AC5D45] ring-2 ring-[#AC5D45]/40"
            : cellTone(d.status, d.booking_source);
          // Cells now show color only — no guest initial letter.
          // ✕ marker is shown for ANY "not bookable" state (sold-out,
          // manual block, external without local guest, OTA block).
          const isLocked = ["sold_out", "blocked", "external", "ota_block"].includes(d.status);
          const label = isLocked ? "✕" : "";
          // OTA lock badge (Airbnb=pink "A" above the cell, Booking=sky "B" below).
          // Per user feedback: the badge MUST NOT overlap the cell to keep
          // the color readable. So we stack: A-badge / cell / B-badge.
          const overlay = d.ota_block_overlay || (d.status === "external" || d.status === "ota_block" ? d.source : "");
          // A real occupant (manual block / walk-in / direct / OTA booking)
          // closes the date on BOTH Airbnb and Booking — so render BOTH lock
          // badges. This matches the Yearly Calendar and the dashboard widget.
          const isRealOccupant = ["sold_out", "blocked", "booked", "external"].includes(d.status);
          const isAirbnb = isRealOccupant || (overlay || "").toLowerCase().includes("airbnb");
          const isBookingOTA = isRealOccupant || (overlay || "").toLowerCase().includes("booking");
          return (
            <td key={d.date} className="p-0.5 border-r border-[#E8E2D9] min-w-[30px] align-middle">
              <div className="flex flex-col items-center gap-[1px]">
                {/* Top slot: Airbnb lock (when applicable) */}
                <div className="h-[10px] flex items-center justify-center">
                  {isAirbnb && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[7px] font-extrabold rounded px-0.5 border-[1.5px] bg-pink-100 border-pink-500 text-pink-700 leading-none"
                      data-testid={`pms-ota-lock-airbnb-${room.slug}-${d.date}`}
                      title={`🔒 Chiuso su ${overlay}`}
                    >
                      <Lock className="w-1.5 h-1.5 text-amber-500" strokeWidth={3} />A
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onToggleCell?.(room, d)}
                  onPointerDown={(e) => onStartDragSel?.(room, d, e)}
                  onPointerEnter={(e) => {
                    if (e.buttons || e.pointerType === "touch") onExtendDragSel?.(room, d);
                  }}
                  onTouchMove={(e) => {
                    const touch = e.touches[0];
                    if (!touch) return;
                    const el = document.elementFromPoint(touch.clientX, touch.clientY);
                    const date = el?.closest?.("[data-pms-date]")?.getAttribute("data-pms-date");
                    if (!date) return;
                    const day = room.days.find((x) => x.date === date);
                    if (day) onExtendDragSel?.(room, day);
                  }}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDoubleClickCell?.(room, d);
                  }}
                  title={tooltipFor(d)}
                  data-testid={`pms-${room.slug}-${d.date}`}
                  data-pms-date={d.date}
                  className={`w-full h-6 rounded border ${tone} flex items-center justify-center text-[10px] font-bold cursor-pointer transition hover:scale-[1.05] select-none touch-none`}
                >
                  {isSel ? "✓" : label}
                </button>
                {/* Bottom slot: Booking lock (when applicable) */}
                <div className="h-[10px] flex items-center justify-center">
                  {isBookingOTA && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[7px] font-extrabold rounded px-0.5 border-[1.5px] bg-sky-100 border-sky-500 text-sky-700 leading-none"
                      data-testid={`pms-ota-lock-booking-${room.slug}-${d.date}`}
                      title={`🔒 Chiuso su ${overlay}`}
                    >
                      <Lock className="w-1.5 h-1.5 text-amber-500" strokeWidth={3} />B
                    </span>
                  )}
                </div>
              </div>
            </td>
          );
        })}
      </tr>
      {/* Status sub-row: physical room numbers — READ-ONLY.
          For every occupied unit on a date we display either the assigned
          physical room number ("n13") or "n?" when not yet assigned. Slots
          are stacked vertically so it's immediately clear how many guests
          still need a room. Hover shows full booking details prefixed by
          the physical number. */}
      <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
        <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59] italic align-top">
          ↳ Camera fisica
        </td>
        {room.days.map((d) => {
          const slots = (d.physical_slots && d.physical_slots.length > 0)
            ? d.physical_slots
            : [];
          return (
            <td
              key={`grn-${d.date}`}
              className="text-center border-r border-[#E8E2D9] py-1 min-w-[30px] text-[#1E3A5F] font-semibold align-top"
            >
              {slots.length === 0 ? (
                <span className="text-[#E8E2D9]">—</span>
              ) : (
                <div className="flex flex-col items-center gap-0.5">
                  {slots.map((s, i) => {
                    const num = s.guide_room_number;
                    // Compact display: number + source letter color-coded.
                    // Examples: "13B" (Booking), "13W" (walk-in), "?A" (Airbnb).
                    const numLabel = num ? String(num) : "?";
                    const src = (s.source || "").toLowerCase();
                    let srcLetter = "";
                    let srcCls = "";
                    if (src.includes("walk")) { srcLetter = "W"; srcCls = "text-orange-600"; }
                    else if (src.includes("airbnb")) { srcLetter = "A"; srcCls = "text-pink-700"; }
                    else if (src.includes("booking")) { srcLetter = "B"; srcCls = "text-sky-700"; }
                    else if (src.includes("website") || src.includes("direct") || src === "sito") { srcLetter = "S"; srcCls = "text-emerald-700"; }
                    const guestName = typeof s.guest === "string"
                      ? s.guest
                      : `${s.guest?.first_name || ""} ${s.guest?.last_name || ""}`.trim();
                    const tip = [
                      num ? `n${num}` : "n?",
                      srcLetter,
                      guestName,
                      s.code,
                    ].filter(Boolean).join(" · ");
                    return (
                      <span
                        key={`${d.date}-slot-${i}`}
                        title={tip}
                        className="inline-flex items-center justify-center gap-0.5 leading-none"
                      >
                        <span className={num ? "text-[#1E3A5F]" : "text-[#AC5D45]"}>
                          {numLabel}
                        </span>
                        {srcLetter && (
                          <span className={`${srcCls} font-extrabold text-[8px]`}>
                            {srcLetter}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
            </td>
          );
        })}
      </tr>

      {/* ==== UNITS AVAILABLE group ==== */}
      <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/30">
        <SectionLabel id="units" hint="Doppio click per modificare">Disponibilità</SectionLabel>
        {room.days.map((d) => (
          <EditableCell
            key={d.date}
            value={d.units_total}
            type="number"
            min={0}
            placeholder="0"
            cellClassName={`text-center border-r border-[#E8E2D9] py-1 ${
              d.units_available === 0 ? "text-rose-700 font-bold" : "text-[#1E3A5F]"
            }`}
            onSave={(v) => onUpdateCell?.(room, d.date, { units_available: v })}
            testid={`cell-units-${room.slug}-${d.date}`}
            tooltip={`In vendita: ${d.units_total} · Libere oggi: ${d.units_available}`}
          />
        ))}
      </tr>
      {/* Units sub-row: total units (read-only) */}
      {expanded.units && (
        <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
          <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59] italic">
            ↳ Camere libere oggi
          </td>
          {room.days.map((d) => (
            <td
              key={d.date}
              className={`text-center border-r border-[#E8E2D9] py-1 min-w-[30px] ${
                d.units_available === 0 ? "text-rose-600 font-semibold" : "text-emerald-700 font-semibold"
              }`}
              title={`${d.units_available} libere su ${d.units_total} in vendita (${d.units_booked || 0} occupate)`}
            >
              {d.units_available}
            </td>
          ))}
        </tr>
      )}

      {/* ==== PRICE group ==== */}
      <tr className="border-b border-[#E8E2D9]">
        <SectionLabel id="price" hint="Doppio click per personalizzare">Prezzo €</SectionLabel>
        {room.days.map((d) => (
          <EditableCell
            key={d.date}
            value={d.price ?? room.price_per_night}
            type="number"
            min={0}
            step="0.01"
            placeholder={String(room.price_per_night)}
            cellClassName={`text-center border-r border-[#E8E2D9] py-1 ${
              d.missing_price ? "text-[#1E3A5F]/60" : d.price !== null && d.price !== room.price_per_night ? "bg-amber-50 text-amber-900 font-semibold" : "text-[#1E3A5F]"
            }`}
            onSave={(v) => onUpdateCell?.(room, d.date, { price: v })}
            testid={`cell-price-${room.slug}-${d.date}`}
            tooltip={d.missing_price ? "Prezzo base (clicca per personalizzare)" : ""}
          />
        ))}
      </tr>
      {/* Price sub-row: base price reference (read-only) */}
      {expanded.price && (
        <>
          <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
            <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59] italic">
              ↳ Prezzo base
            </td>
            {room.days.map((d) => (
              <td key={d.date} className="text-center border-r border-[#E8E2D9] py-1 min-w-[30px] text-[#635F59]">
                €{room.price_per_night}
              </td>
            ))}
          </tr>
          {/* One sub-row per active Rate Plan that applies to this room */}
          {ratePlans
            .filter((rp) => !rp.room_ids?.length || rp.room_ids.includes(room.id))
            .map((rp) => (
              <tr
                key={`rp-${rp.id}`}
                className="border-b border-[#E8E2D9] bg-blue-50/40 text-[9px]"
                data-testid={`pms-${room.slug}-rate-plan-${rp.id}`}
              >
                <td className="sticky left-0 bg-blue-50/40 z-10 px-4 py-1 border-r border-[#E8E2D9]">
                  <div className="flex items-center gap-1 text-[#1E3A5F]">
                    <Tag className="w-2.5 h-2.5 text-blue-600" />
                    <span className="font-semibold">{rp.name}</span>
                  </div>
                  <div className="text-[#635F59] italic">
                    {rp.discount_pct === 0
                      ? "= Standard"
                      : rp.discount_pct < 0
                      ? `${Math.abs(rp.discount_pct)}% sconto`
                      : `+${rp.discount_pct}%`}
                    {rp.min_stay > 0 && ` · min ${rp.min_stay}n`}
                  </div>
                </td>
                {room.days.map((d) => {
                  const base = d.price ?? room.price_per_night;
                  const planPrice = Math.round(base * (1 + rp.discount_pct / 100) * 100) / 100;
                  const cheaper = rp.discount_pct < 0;
                  const isStandard = rp.discount_pct === 0;
                  // iter120: detect if this rate plan is CLOSED for this date.
                  const isClosed = Array.isArray(d.closed_rate_plans) && d.closed_rate_plans.includes(rp.id);
                  // iter121: during an active drag on THIS room+plan, highlight
                  // the cells the cursor crossed even before mouseup, so the
                  // user gets immediate visual feedback ("I'm closing 5 days").
                  const inActiveDrag = rpDrag?.active
                    && rpDrag.roomId === room.id
                    && rpDrag.planId === rp.id
                    && rpDrag.dates?.has(d.date);
                  const previewClose = inActiveDrag && rpDrag.mode === "close";
                  const previewOpen = inActiveDrag && rpDrag.mode === "open";

                  // Editing this rate plan cell = override the BASE price for that date
                  // (only available on Standard, the other plans are computed from it).
                  const handleSaveRatePlanCell = (newPrice) => {
                    const factor = 1 + rp.discount_pct / 100;
                    if (factor <= 0) return;
                    const newBase = Math.round((newPrice / factor) * 100) / 100;
                    onUpdateCell?.(room, d.date, { price: newBase });
                  };

                  // Pointer handlers: start a drag on mousedown/touchstart,
                  // extend on mouseenter/touchmove. Mouse + touch share the
                  // same logic; for touch we resolve the element under the
                  // moving finger via document.elementFromPoint.
                  const onDown = (ev) => {
                    onStartRpDrag?.(room, rp, d, isClosed, ev);
                  };
                  const onEnter = () => {
                    onExtendRpDrag?.(room, rp, d);
                  };
                  const onTouchMove = (ev) => {
                    if (!rpDrag?.active) return;
                    const t = ev.touches?.[0];
                    if (!t) return;
                    const el = document.elementFromPoint(t.clientX, t.clientY);
                    if (!el) return;
                    const dateAttr = el.closest?.("[data-rp-date]")?.getAttribute?.("data-rp-date");
                    if (dateAttr && dateAttr !== d.date) {
                      // Find the day object matching `dateAttr` to feed into extendRpDrag
                      const targetDay = room.days.find((x) => x.date === dateAttr);
                      if (targetDay) onExtendRpDrag?.(room, rp, targetDay);
                    }
                  };

                  // Visual class — closed cells have rose background + line-through.
                  // Preview-close: lighter rose. Preview-open: lighter emerald.
                  let extraCls = "";
                  if (isClosed && !previewOpen) {
                    extraCls = "bg-rose-100 text-rose-700 line-through";
                  } else if (previewClose) {
                    extraCls = "bg-rose-200/70 text-rose-800";
                  } else if (previewOpen) {
                    extraCls = "bg-emerald-200/70 text-emerald-800";
                  } else if (cheaper) {
                    extraCls = "text-emerald-700";
                  } else if (rp.discount_pct > 0) {
                    extraCls = "text-amber-700";
                  } else {
                    extraCls = "text-[#1E3A5F]";
                  }

                  // Only Standard supports double-click editing; the other plans
                  // are read-only derivatives.
                  if (isStandard && !isClosed && !previewClose && !previewOpen) {
                    return (
                      <td
                        key={d.date}
                        data-rp-date={d.date}
                        data-testid={`pms-${room.slug}-${rp.id}-${d.date}`}
                        onMouseDown={onDown}
                        onMouseEnter={onEnter}
                        onTouchStart={onDown}
                        onTouchMove={onTouchMove}
                        onClick={(ev) => {
                          // Single click for editing if it's a pure click
                          // (no drag happened). We detect "drag happened" by
                          // checking the global rpDrag state: if the user
                          // released after touching only this date, dates.size
                          // === 1 and we treat it as click-to-edit.
                          ev.stopPropagation();
                          // Defer the prompt to next tick so any pending drag
                          // finish has time to clear and not interfere.
                          setTimeout(() => {
                            const v = window.prompt(`Nuovo prezzo per ${rp.name} il ${d.date}`, String(planPrice));
                            if (v === null) return;
                            const n = parseFloat(v.replace(",", "."));
                            if (!Number.isFinite(n) || n < 0) {
                              toast.error("Prezzo non valido");
                              return;
                            }
                            handleSaveRatePlanCell(n);
                          }, 50);
                        }}
                        title={`${rp.name} · click per modificare il prezzo · trascina per CHIUDERE/APRIRE la tariffa su più giorni`}
                        className={`text-center border-r border-[#E8E2D9] py-1 min-w-[30px] font-semibold cursor-pointer select-none ${extraCls}`}
                      >
                        €{planPrice}
                      </td>
                    );
                  }
                  // Non-standard plans: NO price editing, only close/open by drag.
                  return (
                    <td
                      key={d.date}
                      data-rp-date={d.date}
                      data-testid={`pms-${room.slug}-${rp.id}-${d.date}${isClosed ? "-closed" : ""}`}
                      onMouseDown={onDown}
                      onMouseEnter={onEnter}
                      onTouchStart={onDown}
                      onTouchMove={onTouchMove}
                      title={isClosed
                        ? `${rp.name} CHIUSA il ${d.date} — trascina per riaprire`
                        : `${rp.name} · trascina mouse/dito su più celle per CHIUDERE la tariffa (i clienti non la vedranno)`}
                      className={`text-center border-r border-[#E8E2D9] py-1 min-w-[30px] font-semibold cursor-pointer select-none ${extraCls}`}
                    >
                      €{planPrice}
                    </td>
                  );
                })}
              </tr>
            ))}
          {ratePlans.filter((rp) => !rp.room_ids?.length || rp.room_ids.includes(room.id)).length === 0 && (
            <tr className="border-b border-[#E8E2D9] bg-blue-50/30 text-[9px]">
              <td className="sticky left-0 bg-blue-50/30 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59] italic" colSpan={room.days.length + 1}>
                ↳ Nessun piano tariffario attivo per questa camera · <a href="/admin/dashboard" className="text-blue-700 hover:underline">Configura</a>
              </td>
            </tr>
          )}
          <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
            <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59]">
              <span className="italic">↳ Sconto promo %</span>
              <button type="button" onClick={() => clearRow({ promo_discount_pct: 0 }, "Sconto promo")} className="ml-2 text-[9px] text-rose-700 hover:underline" title="Resetta tutto il mese">pulisci</button>
            </td>
            {room.days.map((d) => (
              <EditableCell
                key={d.date}
                value={d.promo_discount_pct || ""}
                type="number"
                min={0}
                step="1"
                placeholder="—"
                displayEmpty="—"
                cellClassName="text-center border-r border-[#E8E2D9] py-1 text-pink-700 font-bold min-w-[30px]"
                onSave={(v) => onUpdateCell?.(room, d.date, { promo_discount_pct: parseFloat(v) || 0 })}
                testid={`pms-promopct-${room.slug}-${d.date}`}
                tooltip="Sconto promo % · doppio click per modificare"
              />
            ))}
          </tr>
        </>
      )}

      {/* ==== MIN STAY group ==== */}
      <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/30">
        <SectionLabel id="minstay" hint="Doppio click per modificare">Min. notti</SectionLabel>
        {room.days.map((d) => (
          <EditableCell
            key={d.date}
            value={d.min_stay || ""}
            type="number"
            min={0}
            placeholder="—"
            displayEmpty="—"
            cellClassName="text-center border-r border-[#E8E2D9] py-1 text-[#1E3A5F]"
            onSave={(v) => onUpdateCell?.(room, d.date, { min_stay: v })}
            testid={`cell-minstay-${room.slug}-${d.date}`}
          />
        ))}
      </tr>
      {/* Min stay sub-row */}
      {expanded.minstay && (
        <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
          <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59]">
            <span className="italic">↳ Max. notti</span>
            <button type="button" onClick={() => clearRow({ max_stay: 0 }, "Max. notti")} className="ml-2 text-[9px] text-rose-700 hover:underline" title="Resetta tutto il mese">pulisci</button>
          </td>
          {room.days.map((d) => (
            <EditableCell
              key={d.date}
              value={d.max_stay || ""}
              type="number"
              min={0}
              placeholder="∞"
              displayEmpty="∞"
              cellClassName="text-center border-r border-[#E8E2D9] py-1 text-[#635F59] min-w-[30px]"
              onSave={(v) => onUpdateCell?.(room, d.date, { max_stay: parseInt(v, 10) || 0 })}
              testid={`pms-maxstay-${room.slug}-${d.date}`}
              tooltip="Max notti · doppio click per modificare (∞ = nessun massimo)"
            />
          ))}
        </tr>
      )}

      {/* ==== RESTRICTIONS group ==== */}
      <tr className={expanded.rest ? "" : "border-b-0"}>
        <SectionLabel id="rest" hint="CTA = no check-in · CTD = no check-out">Restrizioni</SectionLabel>
        {room.days.map((d) => {
          const flags = [];
          if (d.closed_to_arrival) flags.push("CTA");
          if (d.closed_to_departure) flags.push("CTD");
          if (d.min_advance_days) flags.push(`+${d.min_advance_days}g`);
          if (d.promo_label) flags.push("PROMO");
          return (
            <td
              key={d.date}
              className="text-center border-r border-[#E8E2D9] py-1 text-[9px] min-w-[30px]"
              title={[
                d.closed_to_arrival && "Chiuso all'arrivo",
                d.closed_to_departure && "Chiuso alla partenza",
                d.min_advance_days && `Min ${d.min_advance_days}g anticipo`,
                d.promo_label && d.promo_label,
              ].filter(Boolean).join(" · ")}
            >
              {flags.length > 0 ? (
                <span className="inline-flex flex-wrap gap-0.5 justify-center">
                  {flags.map((f) => (
                    <span
                      key={f}
                      className={`px-1 rounded font-bold ${
                        f === "PROMO"
                          ? "bg-fuchsia-600 text-white"
                          : "bg-violet-100 text-violet-800"
                      }`}
                    >
                      {f}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-[#635F59]/30">—</span>
              )}
            </td>
          );
        })}
      </tr>
      {/* Restrictions sub-rows: one row per restriction type — cliccabili per selezionare la cella e poi modificare via bulk */}
      {expanded.rest && (
        <>
          <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
            <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59]">
              <span className="italic">↳ Chiuso all'arrivo</span>
              <button type="button" onClick={() => clearRow({ closed_to_arrival: false }, "Chiuso all'arrivo")} className="ml-2 text-[9px] text-rose-700 hover:underline" title="Resetta tutto il mese">pulisci</button>
            </td>
            {room.days.map((d) => {
              const isSel = !!selected?.[cellKey(d.date)];
              return (
                <td key={d.date} className="p-0.5 border-r border-[#E8E2D9] min-w-[30px]">
                  <button
                    type="button"
                    onClick={() => onToggleCell?.(room, d)}
                    onDoubleClick={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      onUpdateCell?.(room, d.date, { closed_to_arrival: !d.closed_to_arrival });
                    }}
                    className={`w-full h-5 rounded text-[9px] font-bold transition hover:bg-rose-50 ${isSel ? "bg-[#AC5D45] text-white ring-1 ring-[#AC5D45]" : "text-rose-700"}`}
                    title="Click=seleziona · Doppio click=attiva/disattiva"
                    data-testid={`pms-cta-${room.slug}-${d.date}`}
                  >
                    {d.closed_to_arrival ? "✕" : <span className="text-[#E8E2D9]">—</span>}
                  </button>
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
            <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59]">
              <span className="italic">↳ Chiuso alla partenza</span>
              <button type="button" onClick={() => clearRow({ closed_to_departure: false }, "Chiuso alla partenza")} className="ml-2 text-[9px] text-rose-700 hover:underline" title="Resetta tutto il mese">pulisci</button>
            </td>
            {room.days.map((d) => {
              const isSel = !!selected?.[cellKey(d.date)];
              return (
                <td key={d.date} className="p-0.5 border-r border-[#E8E2D9] min-w-[30px]">
                  <button
                    type="button"
                    onClick={() => onToggleCell?.(room, d)}
                    onDoubleClick={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      onUpdateCell?.(room, d.date, { closed_to_departure: !d.closed_to_departure });
                    }}
                    className={`w-full h-5 rounded text-[9px] font-bold transition hover:bg-rose-50 ${isSel ? "bg-[#AC5D45] text-white ring-1 ring-[#AC5D45]" : "text-rose-700"}`}
                    title="Click=seleziona · Doppio click=attiva/disattiva"
                    data-testid={`pms-ctd-${room.slug}-${d.date}`}
                  >
                    {d.closed_to_departure ? "✕" : <span className="text-[#E8E2D9]">—</span>}
                  </button>
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[#E8E2D9] bg-[#FDFBF7]/40 text-[9px]">
            <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59]">
              <span className="italic">↳ Anticipo min.</span>
              <button type="button" onClick={() => clearRow({ min_advance_days: 0 }, "Anticipo min.")} className="ml-2 text-[9px] text-rose-700 hover:underline" title="Resetta tutto il mese">pulisci</button>
            </td>
            {room.days.map((d) => (
              <EditableCell
                key={d.date}
                value={d.min_advance_days || ""}
                type="number"
                min={0}
                placeholder="—"
                displayEmpty="—"
                cellClassName="text-center border-r border-[#E8E2D9] py-1 text-[#635F59] min-w-[30px]"
                onSave={(v) => onUpdateCell?.(room, d.date, { min_advance_days: parseInt(v, 10) || 0 })}
                testid={`pms-advance-${room.slug}-${d.date}`}
                tooltip="Giorni di anticipo minimo · doppio click per modificare"
              />
            ))}
          </tr>
          <tr className="bg-[#FDFBF7]/40 text-[9px]">
            <td className="sticky left-0 bg-[#FDFBF7]/40 z-10 px-4 py-1 border-r border-[#E8E2D9] text-[#635F59]">
              <span className="italic">↳ Promo / Last min.</span>
              <button type="button" onClick={() => clearRow({ promo_label: "", promo_discount_pct: 0 }, "Promo / Last min.")} className="ml-2 text-[9px] text-rose-700 hover:underline" title="Resetta tutto il mese">pulisci</button>
            </td>
            {room.days.map((d) => {
              const isSel = !!selected?.[cellKey(d.date)];
              const editPromo = () => {
                const current = d.promo_label || "";
                const next = window.prompt("Etichetta promo (vuoto per rimuovere)", current);
                if (next === null) return;
                const trimmed = next.trim();
                onUpdateCell?.(room, d.date, trimmed === "" ? { promo_label: "", promo_discount_pct: 0 } : { promo_label: trimmed });
              };
              return (
                <td key={d.date} className="p-0.5 border-r border-[#E8E2D9] min-w-[30px]" title={d.promo_label || ""}>
                  <button
                    type="button"
                    onClick={() => onToggleCell?.(room, d)}
                    onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); editPromo(); }}
                    className={`w-full h-5 rounded text-[9px] font-bold transition hover:bg-pink-50 ${isSel ? "bg-[#AC5D45] text-white ring-1 ring-[#AC5D45]" : "text-pink-700"}`}
                    title="Click=seleziona · Doppio click=modifica etichetta"
                    data-testid={`pms-promo-${room.slug}-${d.date}`}
                  >
                    {d.promo_label ? d.promo_label.slice(0, 6) : <span className="text-[#E8E2D9]">—</span>}
                  </button>
                </td>
              );
            })}
          </tr>
        </>
      )}
    </>
  );
}

// Inline-editable cell with double-click to enter edit mode (or single click on dash placeholders)
function EditableCell({
  value,
  type = "text",
  min,
  step,
  placeholder,
  displayEmpty,
  cellClassName = "",
  onSave,
  testid,
  tooltip = "",
  onMouseDown,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    if (String(draft) === String(value)) return;
    const v = type === "number" ? (draft === "" ? 0 : parseFloat(draft)) : draft;
    onSave?.(v);
  };

  if (editing) {
    return (
      <td className={cellClassName}>
        <input
          autoFocus
          type={type}
          min={min}
          step={step}
          value={draft ?? ""}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => { try { e.target.select(); } catch { /* noop */ } }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") { setDraft(value); setEditing(false); }
          }}
          data-testid={`${testid}-input`}
          className="w-full h-6 text-center text-xs bg-white border border-[#AC5D45] rounded outline-none px-0.5"
        />
      </td>
    );
  }

  return (
    <td
      className={`${cellClassName} cursor-pointer hover:bg-[#FDFBF7] transition`}
      onMouseDown={onMouseDown}
      onClick={() => setEditing(true)}
      title={tooltip || "Clicca per modificare"}
      data-testid={testid}
    >
      {value === "" || value === null || value === undefined ? (displayEmpty ?? placeholder ?? "—") : value}
    </td>
  );
}

function tooltipFor(d) {
  const date = format(new Date(d.date), "dd/MM/yyyy");
  // For booked dates, prefix the physical room number ("n13") at the very
  // start of the line so the host can quickly see which physical room the
  // guest is in without opening the booking detail.
  if (d.status === "booked") {
    const roomNum = d.guide_room_number;
    const roomTag = roomNum ? `n${roomNum} · ` : "n? · ";
    return `${roomTag}${date} — ${d.guest || ""}${d.code ? ` (${d.code})` : ""}${d.booking_source ? ` · ${d.booking_source}` : ""}`;
  }
  if (d.status === "external") return `${date} — ${d.source || "Esterno"}: ${d.summary || ""}`;
  if (d.status === "ota_block") return `${date} — Chiuso su ${d.source || "OTA"}`;
  if (d.status === "sold_out") return `${date} — Tutto esaurito (${d.units_booked}/${d.units_total} unità)`;
  if (d.status === "blocked") return `${date} — Chiuso manualmente`;
  return `${date} — ${d.units_available}/${d.units_total} unità libere`;
}

// --- Mobile mini-calendar per room (image 1 style) -----------
function RoomMiniCalendar({ room, month, monthDays, selected, onToggleCell, onDoubleClickCell, onStartDragSel, onExtendDragSel }) {
  const startWeekday = (getDay(startOfMonth(month)) + 6) % 7; // Mon=0
  const padding = Array.from({ length: startWeekday });
  const dayMap = useMemo(() => {
    const m = {};
    room.days.forEach((d) => {
      m[d.date] = d;
    });
    return m;
  }, [room.days]);

  return (
    <section
      className="border border-[#E8E2D9] rounded-2xl p-4 bg-white shadow-sm"
      data-testid={`mini-cal-${room.slug}`}
    >
      <h3 className="font-display text-lg text-[#1E3A5F] mb-3">{room.name?.it}</h3>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-[#635F59] uppercase font-semibold mb-1">
        {DAYS_FULL_IT.map((d) => (
          <div key={d} className="text-center">
            {d.charAt(0)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {padding.map((_, i) => (
          <div key={`p-${room.id}-${i}`} />
        ))}
        {monthDays.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const info = dayMap[key];
          const status = info?.status || "free";
          const isSel = !!selected?.[`${room.id}|${key}`];
          const tone = isSel ? "bg-[#AC5D45] text-white border-[#AC5D45] ring-2 ring-[#AC5D45]/40" : cellTone(status, info?.booking_source);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggleCell?.(room, info || { date: key, status: "free" })}
              onPointerDown={(e) => onStartDragSel?.(room, info || { date: key, status: "free" }, e)}
              onPointerEnter={(e) => {
                if (e.buttons || e.pointerType === "touch") onExtendDragSel?.(room, info || { date: key, status: "free" });
              }}
              onTouchMove={(e) => {
                const touch = e.touches[0];
                if (!touch) return;
                const el = document.elementFromPoint(touch.clientX, touch.clientY);
                const date = el?.closest?.("[data-mini-date]")?.getAttribute("data-mini-date");
                if (!date) return;
                onExtendDragSel?.(room, dayMap[date] || { date, status: "free" });
              }}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDoubleClickCell?.(room, info || { date: key, status: "free" });
              }}
              className={`aspect-square rounded-md border text-[11px] font-bold flex items-center justify-center transition active:scale-95 select-none touch-none ${tone}`}
              data-testid={`mini-${room.slug}-${key}`}
              data-mini-date={key}
            >
              {format(d, "d")}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// --- Mobile bookings calendar with dots (image 2 style) ------
function BookingsDotsCalendar({ month, monthDays, bookingsByDate, onPickDay }) {
  const startWeekday = (getDay(startOfMonth(month)) + 6) % 7;
  const padding = Array.from({ length: startWeekday });
  return (
    <section className="border border-[#E8E2D9] rounded-2xl p-3 bg-white shadow-sm">
      <div className="grid grid-cols-7 gap-0.5 text-[10px] text-[#635F59] uppercase font-semibold mb-2 text-center">
        {DAYS_FULL_IT.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {padding.map((_, i) => (
          <div key={`pp-${i}`} className="aspect-square" />
        ))}
        {monthDays.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const items = bookingsByDate[key] || [];
          const internal = items.filter((it) => it.day.status === "booked").length;
          const external = items.filter((it) => it.day.status === "external").length;
          return (
            <button
              key={key}
              type="button"
              data-testid={`dots-${key}`}
              onClick={() => onPickDay({ date: key, items })}
              className={`aspect-square rounded-md flex flex-col items-center justify-center text-[#1E3A5F] hover:bg-[#FDFBF7] transition border border-transparent ${
                isToday(d) ? "bg-[#1E3A5F] text-white hover:!bg-[#152A45]" : ""
              }`}
            >
              <span className={`text-sm ${isToday(d) ? "font-bold" : ""}`}>{format(d, "d")}</span>
              {(internal > 0 || external > 0) && (
                <span className="flex items-center gap-0.5 mt-0.5">
                  {Array.from({ length: Math.min(internal, 3) }).map((_, i) => (
                    <span
                      key={`int-${i}`}
                      className={`w-1.5 h-1.5 rounded-full ${
                        isToday(d) ? "bg-white" : "bg-rose-500"
                      }`}
                    />
                  ))}
                  {Array.from({ length: Math.min(external, 3) }).map((_, i) => (
                    <span
                      key={`ext-${i}`}
                      className={`w-1.5 h-1.5 rounded-full ${
                        isToday(d) ? "bg-white" : "bg-sky-500"
                      }`}
                    />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-[#635F59] mt-3 text-center">
        Tocca un giorno per vedere le prenotazioni
      </p>
    </section>
  );
}

// --- iCal Config Dialog --------------------------------------


// --- Bulk edit drawer (multi-room, multi-date) ---------------
function BulkEditDrawer({ open, onOpenChange, cells, onApplied }) {
  const [section, setSection] = useState("prices"); // units | prices | status | restrictions
  // Units
  const [unitsAvail, setUnitsAvail] = useState("");
  // Prices
  const [price, setPrice] = useState("");
  const [promoLabel, setPromoLabel] = useState("");
  const [promoType, setPromoType] = useState("");
  const [promoPct, setPromoPct] = useState("");
  // Status
  const [blocked, setBlocked] = useState(null); // null = no change, true = block, false = open
  // Restrictions
  const [minStay, setMinStay] = useState("");
  const [maxStay, setMaxStay] = useState("");
  const [minAdvance, setMinAdvance] = useState("");
  const [cta, setCta] = useState(false);
  const [ctd, setCtd] = useState(false);
  // Days of week
  const [allDays, setAllDays] = useState(true);
  const [dows, setDows] = useState([true, true, true, true, true, true, true]); // Mon..Sun
  const [submitting, setSubmitting] = useState(false);
  // Manual override of the date range (lets user extend beyond the selected cells)
  const [manualFrom, setManualFrom] = useState("");
  const [manualTo, setManualTo] = useState("");

  // Auto-computed range from the selected cells (used as default)
  const autoRange = useMemo(() => {
    const all = cells.map((c) => c.date).sort();
    if (all.length === 0) return null;
    return { from: all[0], to: all[all.length - 1] };
  }, [cells]);

  // Snapshot of CURRENT values across the selected cells. Pre-fills the
  // drawer so the host sees what's already set (and renders "40 – 60" when
  // the selection spans dates with different prices). Without this, the
  // drawer is blank and any edit silently overwrites whatever was there.
  const summary = useMemo(() => {
    if (!cells.length) return null;
    const r = (key, fmt = (v) => v) => {
      const vals = cells.map((c) => c[key]).filter((v) => v !== null && v !== undefined);
      if (vals.length === 0) return null;
      const min = Math.min(...vals.map(Number));
      const max = Math.max(...vals.map(Number));
      return min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
    };
    const blockedVals = cells.map((c) => !!c.blocked);
    const ctaVals = cells.map((c) => !!c.closed_to_arrival);
    const ctdVals = cells.map((c) => !!c.closed_to_departure);
    const promoLabels = [...new Set(cells.map((c) => c.promo_label || "").filter(Boolean))];
    return {
      price: r("price", (v) => `€${Number(v).toFixed(2)}`),
      units: r("units_available", (v) => `${v}`),
      minStay: r("min_stay", (v) => v),
      maxStay: r("max_stay", (v) => v),
      minAdvance: r("min_advance", (v) => v),
      blocked: blockedVals.every((v) => v) ? "tutti bloccati"
        : blockedVals.every((v) => !v) ? "tutti aperti"
        : "misti (alcuni bloccati)",
      cta: ctaVals.every((v) => v) ? "tutti CTA"
        : ctaVals.every((v) => !v) ? "nessuno"
        : "misto",
      ctd: ctdVals.every((v) => v) ? "tutti CTD"
        : ctdVals.every((v) => !v) ? "nessuno"
        : "misto",
      promos: promoLabels.length === 0 ? "nessuna" : promoLabels.length === 1 ? promoLabels[0] : `misti (${promoLabels.length})`,
    };
  }, [cells]);

  useEffect(() => {
    if (open) {
      setSection("prices");
      setUnitsAvail(""); setPrice(""); setPromoLabel(""); setPromoType(""); setPromoPct("");
      setBlocked(null);
      setMinStay(""); setMaxStay(""); setMinAdvance(""); setCta(false); setCtd(false);
      setAllDays(true); setDows([true, true, true, true, true, true, true]);
      // Pre-fill manual from/to with the auto-computed range
      setManualFrom(autoRange?.from || "");
      setManualTo(autoRange?.to || "");
    }
  }, [open, autoRange]);

  // Group cells by roomId — used to know which rooms to apply changes to
  const groups = useMemo(() => {
    const map = {};
    cells.forEach((c) => {
      if (!map[c.roomId]) map[c.roomId] = { roomId: c.roomId, roomName: c.roomName, dates: [] };
      map[c.roomId].dates.push(c.date);
    });
    Object.values(map).forEach((g) => g.dates.sort());
    return Object.values(map);
  }, [cells]);

  // Effective date range: manual override (if both filled) wins over auto
  const dateRange = useMemo(() => {
    if (manualFrom && manualTo) {
      // Normalise order
      const f = manualFrom <= manualTo ? manualFrom : manualTo;
      const t = manualFrom <= manualTo ? manualTo : manualFrom;
      return { from: f, to: t };
    }
    return autoRange;
  }, [manualFrom, manualTo, autoRange]);

  // Approximate number of nights covered by the effective range (inclusive)
  const rangeNights = useMemo(() => {
    if (!dateRange) return 0;
    try {
      const a = new Date(dateRange.from + "T00:00:00");
      const b = new Date(dateRange.to + "T00:00:00");
      return Math.max(1, Math.round((b - a) / 86400000) + 1);
    } catch {
      return 0;
    }
  }, [dateRange]);

  const apply = async () => {
    if (groups.length === 0) return;
    if (!dateRange) {
      toast.error("Imposta un intervallo di date valido");
      return;
    }
    setSubmitting(true);
    let totalUpdated = 0;
    try {
      for (const g of groups) {
        // Use the effective range (manual override or auto-computed),
        // not just the selected cell dates — this lets the user apply
        // bulk changes to a longer span than the originally selected cells.
        const fromDate = dateRange.from;
        const toDate = dateRange.to;
        const body = { from_date: fromDate, to_date: toDate };
        // Only honor day-of-week filter if not "all"
        if (!allDays) {
          const allowedDows = dows.map((on, i) => on ? i : -1).filter((i) => i >= 0);
          if (allowedDows.length === 0) {
            toast.error("Seleziona almeno un giorno della settimana");
            setSubmitting(false);
            return;
          }
          body.days_of_week = allowedDows;
        }
        // Section-driven payload: only send fields the user actually touched
        if (section === "units") {
          if (unitsAvail !== "") body.units_available = parseInt(unitsAvail) || 0;
        } else if (section === "prices") {
          if (price !== "") body.price = parseFloat(price);
          if (promoLabel !== "") {
            const cleared = promoLabel.trim() === "—" || promoLabel.trim() === "-";
            body.promo_label = cleared ? "" : promoLabel;
            if (cleared) { body.promo_type = ""; body.promo_discount_pct = 0; }
          }
          if (promoType !== "" && body.promo_label !== "") body.promo_type = promoType;
          if (promoPct !== "" && body.promo_label !== "") body.promo_discount_pct = parseFloat(promoPct) || 0;
        } else if (section === "status") {
          if (blocked === "ignore_ota") {
            // Same as "Apri" but with explicit UX label. Backend
            // automatically activates host_override_ota=true when
            // blocked=false, which keeps the OTA lock badge but lets
            // direct bookings through.
            body.blocked = false;
          } else if (blocked !== null) {
            body.blocked = blocked;
          }
        } else if (section === "restrictions") {
          if (minStay !== "") body.min_stay = parseInt(minStay) || 0;
          if (maxStay !== "") body.max_stay = parseInt(maxStay) || 0;
          if (minAdvance !== "") body.min_advance_days = parseInt(minAdvance) || 0;
          body.closed_to_arrival = cta;
          body.closed_to_departure = ctd;
        }
        // Skip if no real change
        const knownKeys = ["from_date", "to_date", "days_of_week"];
        const hasChange = Object.keys(body).some((k) => !knownKeys.includes(k));
        if (!hasChange) {
          toast.error("Nessuna modifica indicata in questa sezione");
          setSubmitting(false);
          return;
        }
        const res = await api.put(`/admin/calendar/${g.roomId}/bulk`, body);
        totalUpdated += res.data?.updated || 0;
      }
      toast.success(`✓ ${totalUpdated} date aggiornate su ${groups.length} camere`);
      onApplied?.();
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento"));
    } finally {
      setSubmitting(false);
    }
  };

  const sectionTabs = [
    { id: "units", label: "Unità da vendere", help: "Aggiorna il numero di unità in vendita per questa tipologia di alloggio" },
    { id: "prices", label: "Prezzi", help: "Modifica i prezzi (o inserisci una promo) per le date selezionate" },
    { id: "status", label: "Status", help: "Apri o chiudi questo alloggio per le date selezionate" },
    { id: "restrictions", label: "Restrizioni", help: "Min/Max notti · CTA · CTD · Anticipo prenotazione" },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        onPointerDownOutside={(e) => {
          // Don't close the Sheet when the user clicks inside a Radix
          // Select / Popover portal (rendered outside SheetContent DOM).
          const target = e.target;
          if (target && target.closest && target.closest("[data-radix-popper-content-wrapper], [role=listbox], [role=option]")) {
            e.preventDefault();
          }
        }}
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <Wand2 className="w-5 h-5" /> Modifica in blocco
          </SheetTitle>
          <SheetDescription>
            {groups.length} {groups.length === 1 ? "camera" : "camere"} · {rangeNights} {rangeNights === 1 ? "giorno" : "giorni"}
          </SheetDescription>
        </SheetHeader>

        {/* Snapshot of CURRENT values across the selected cells — gives
            the host immediate context so they don't overwrite blindly. */}
        {summary && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="bulk-current-summary">
            <div className="text-[11px] text-blue-900 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              📊 Stato attuale delle date selezionate
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-[#1E3A5F]">
              {summary.price && <div><span className="text-[#635F59]">Prezzo:</span> <strong>{summary.price}</strong></div>}
              {summary.units && <div><span className="text-[#635F59]">Unità:</span> <strong>{summary.units}</strong></div>}
              {summary.minStay && <div><span className="text-[#635F59]">Min notti:</span> <strong>{summary.minStay}</strong></div>}
              {summary.maxStay && <div><span className="text-[#635F59]">Max notti:</span> <strong>{summary.maxStay}</strong></div>}
              <div><span className="text-[#635F59]">Blocchi:</span> <strong>{summary.blocked}</strong></div>
              {summary.promos !== "nessuna" && <div><span className="text-[#635F59]">Promo:</span> <strong>{summary.promos}</strong></div>}
            </div>
            <div className="text-[10px] text-blue-800 mt-2 italic">
              Lascia un campo vuoto per <strong>non modificarlo</strong>.
            </div>
          </div>
        )}

        {/* Editable date range — overrides the auto-computed range from selected cells.
            Lets the user expand the bulk action beyond their initial click selection. */}
        <div className="mt-4 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 space-y-2">
          <div className="text-xs text-[#635F59] font-semibold uppercase tracking-wider">
            Intervallo di date
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-[#635F59]">Da</Label>
              <DateInputIT value={manualFrom}
                onChange={(e) => setManualFrom(e.target.value)}
                data-testid="cm-bulk-from-date"
                className="mt-1 h-9 text-xs font-mono bg-white"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#635F59]">A (incluso)</Label>
              <DateInputIT value={manualTo}
                onChange={(e) => setManualTo(e.target.value)}
                data-testid="cm-bulk-to-date"
                className="mt-1 h-9 text-xs font-mono bg-white"
              />
            </div>
          </div>
          {dateRange && (
            <div className="text-[11px] text-[#1E3A5F]">
              Da <strong>{format(new Date(dateRange.from), "dd/MM/yyyy")}</strong> a <strong>{format(new Date(dateRange.to), "dd/MM/yyyy")}</strong> · <span className="text-[#AC5D45] font-semibold">{rangeNights} {rangeNights === 1 ? "data" : "date"}</span>
            </div>
          )}
        </div>

        {/* Days of week filter */}
        <div className="mt-4 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#635F59] font-semibold uppercase tracking-wider">Giorni della settimana</span>
            <button type="button" onClick={() => setAllDays(!allDays)} className="text-[11px] text-[#AC5D45] hover:underline">
              {allDays ? "Personalizza" : "Tutti"}
            </button>
          </div>
          {allDays ? (
            <p className="text-xs text-[#635F59]">Tutti i giorni dell'intervallo</p>
          ) : (
            <div className="flex gap-1 flex-wrap">
              {["lun","mar","mer","gio","ven","sab","dom"].map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDows((p) => p.map((v, j) => j === i ? !v : v))}
                  className={`px-2 py-1 rounded text-[11px] font-semibold transition ${dows[i] ? "bg-[#1E3A5F] text-white" : "bg-white border border-[#E8E2D9] text-[#635F59]"}`}
                  data-testid={`dow-${d}`}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Affected rooms */}
        <div className="mt-3 text-[11px] bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 max-h-24 overflow-y-auto">
          <div className="font-semibold text-[#635F59] mb-1">{cells.length} celle · {groups.length} {groups.length === 1 ? "camera" : "camere"}</div>
          {groups.map((g) => (
            <div key={g.roomId} className="flex justify-between gap-2 py-0.5">
              <span className="text-[#1E3A5F]">{g.roomName}</span>
              <span className="text-[#635F59]">{g.dates.length} date</span>
            </div>
          ))}
        </div>

        {/* Section accordion */}
        <div className="mt-4 space-y-2">
          {sectionTabs.map((s) => (
            <div key={s.id} className={`border rounded-lg overflow-hidden ${section === s.id ? "border-[#1E3A5F] shadow-sm" : "border-[#E8E2D9]"}`}>
              <button
                type="button"
                onClick={() => setSection(s.id)}
                data-testid={`section-${s.id}`}
                className={`w-full text-left px-4 py-3 flex items-center justify-between transition ${section === s.id ? "bg-[#1E3A5F] text-white" : "bg-white hover:bg-[#FDFBF7]"}`}
              >
                <div>
                  <div className="font-semibold text-sm">{s.label}</div>
                  <div className={`text-[11px] ${section === s.id ? "text-white/70" : "text-[#635F59]"}`}>{s.help}</div>
                </div>
                <span className={`transition transform ${section === s.id ? "rotate-90" : ""}`}>›</span>
              </button>
              {section === s.id && (
                <div className="bg-white p-4 space-y-3" data-testid={`section-body-${s.id}`}>
                  {s.id === "units" && (
                    <div>
                      <Label>Unità in vendita per ogni data</Label>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={unitsAvail}
                        onChange={(e) => setUnitsAvail(e.target.value)}
                        placeholder={summary?.units ? `Attuale: ${summary.units}` : "Es. 2"}
                        data-testid="bulk-units-available"
                        className="mt-1.5 h-11"
                      />
                      {summary?.units && (
                        <p className="text-[11px] text-[#1E3A5F] mt-1 font-semibold">📊 Valore attuale: {summary.units}</p>
                      )}
                      <p className="text-[11px] text-[#635F59] mt-1">Sovrascrive il valore di default della camera per le date selezionate.</p>
                    </div>
                  )}
                  {s.id === "prices" && (
                    <>
                      <div>
                        <Label>Prezzo (€/notte)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          placeholder={summary?.price ? `Attuale: ${summary.price}` : "Lascia vuoto per non modificare"}
                          data-testid="bulk-price"
                          className="mt-1.5 h-11"
                        />
                        {summary?.price && (
                          <p className="text-[11px] text-[#1E3A5F] mt-1 font-semibold">📊 Valore attuale: {summary.price}</p>
                        )}
                      </div>
                      <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/60 p-3 space-y-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-fuchsia-900">
                          <Tag className="w-4 h-4" /> Promo · Last minute · Sconto
                        </div>
                        <Input
                          value={promoLabel}
                          onChange={(e) => setPromoLabel(e.target.value)}
                          placeholder='Etichetta — "—" per rimuovere'
                          data-testid="bulk-promo-label"
                          className="h-10 bg-white"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Select value={promoType || "none"} onValueChange={(v) => setPromoType(v === "none" ? "" : v)}>
                            <SelectTrigger data-testid="bulk-promo-type" className="h-10 bg-white">
                              <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              <SelectItem value="last_minute">Last minute</SelectItem>
                              <SelectItem value="seasonal">Stagionale</SelectItem>
                              <SelectItem value="special">Speciale</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="0"
                            max="90"
                            value={promoPct}
                            onChange={(e) => setPromoPct(e.target.value)}
                            placeholder="Sconto %"
                            data-testid="bulk-promo-pct"
                            className="h-10 bg-white"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {s.id === "status" && (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => setBlocked(false)}
                        data-testid="status-open"
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition ${blocked === false ? "border-emerald-500 bg-emerald-50" : "border-[#E8E2D9] hover:bg-[#FDFBF7]"}`}
                      >
                        <span className="w-3 h-3 rounded-full bg-emerald-500" />
                        <div className="text-left">
                          <div className="font-semibold text-emerald-900">Apri</div>
                          <div className="text-[11px] text-[#635F59]">Le date torneranno disponibili alla prenotazione</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setBlocked(true)}
                        data-testid="status-close"
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition ${blocked === true ? "border-rose-500 bg-rose-50" : "border-[#E8E2D9] hover:bg-[#FDFBF7]"}`}
                      >
                        <span className="w-3 h-3 rounded-full bg-rose-500" />
                        <div className="text-left">
                          <div className="font-semibold text-rose-900">Chiudi</div>
                          <div className="text-[11px] text-[#635F59]">Le date saranno bloccate alla prenotazione</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setBlocked("ignore_ota")}
                        data-testid="status-ignore-ota"
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition ${blocked === "ignore_ota" ? "border-sky-500 bg-sky-50" : "border-[#E8E2D9] hover:bg-[#FDFBF7]"}`}
                      >
                        <span className="w-3 h-3 rounded-full bg-sky-500" />
                        <div className="text-left">
                          <div className="font-semibold text-sky-900">🔓 Riapri ignorando blocco Airbnb/Booking</div>
                          <div className="text-[11px] text-[#635F59]">Tieni il lucchetto come avviso, ma rendi la data prenotabile dal tuo sito</div>
                        </div>
                      </button>
                    </div>
                  )}
                  {s.id === "restrictions" && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Min. notti</Label>
                          <Input type="number" min="0" value={minStay} onChange={(e) => setMinStay(e.target.value)} placeholder={summary?.minStay ? `Attuale: ${summary.minStay}` : "0=off"} data-testid="bulk-min-stay" className="mt-1.5 h-10" />
                          {summary?.minStay && <p className="text-[10px] text-[#1E3A5F] mt-1">📊 {summary.minStay}</p>}
                        </div>
                        <div>
                          <Label className="text-xs">Max. notti</Label>
                          <Input type="number" min="0" value={maxStay} onChange={(e) => setMaxStay(e.target.value)} placeholder={summary?.maxStay ? `Attuale: ${summary.maxStay}` : "0=off"} data-testid="bulk-max-stay" className="mt-1.5 h-10" />
                          {summary?.maxStay && <p className="text-[10px] text-[#1E3A5F] mt-1">📊 {summary.maxStay}</p>}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Anticipo minimo (giorni)</Label>
                        <Input type="number" min="0" value={minAdvance} onChange={(e) => setMinAdvance(e.target.value)} placeholder={summary?.minAdvance ? `Attuale: ${summary.minAdvance}` : "0=off"} data-testid="bulk-min-advance" className="mt-1.5 h-10" />
                        {summary?.minAdvance && <p className="text-[10px] text-[#1E3A5F] mt-1">📊 {summary.minAdvance}</p>}
                      </div>
                      {summary && (summary.cta !== "nessuno" || summary.ctd !== "nessuno") && (
                        <div className="text-[10px] text-[#1E3A5F] bg-[#FDFBF7] border border-[#E8E2D9] rounded px-2 py-1.5">
                          📊 Stato attuale: CTA = {summary.cta} · CTD = {summary.ctd}
                        </div>
                      )}
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[#FDFBF7]">
                          <Checkbox checked={cta} onCheckedChange={(v) => setCta(v === true)} data-testid="bulk-cta" />
                          <span className="text-sm">
                            <strong>Chiuso all'arrivo (CTA)</strong>
                            <div className="text-[11px] text-[#635F59]">Niente nuovi check-in in queste date</div>
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[#FDFBF7]">
                          <Checkbox checked={ctd} onCheckedChange={(v) => setCtd(v === true)} data-testid="bulk-ctd" />
                          <span className="text-sm">
                            <strong>Chiuso alla partenza (CTD)</strong>
                            <div className="text-[11px] text-[#635F59]">Niente nuovi check-out in queste date</div>
                          </span>
                        </label>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-5 sticky bottom-0 bg-white pb-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Annulla</Button>
          <Button
            onClick={apply}
            disabled={submitting}
            data-testid="bulk-apply"
            className="flex-1 bg-[#AC5D45] hover:bg-[#8A4936] text-white"
          >
            {submitting ? "Applico..." : "Applica"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
